import React, { useCallback, useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut,
  signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, updateProfile,
} from 'firebase/auth';
import { collection as firestoreCollection, doc, getDoc, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { functions, auth, db } from './firebase.js';
// Core CSS
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
} from "ag-grid-community";
import Select from 'react-select';

import {
  Typography, Container, Paper, Button, CircularProgress, Tabs, Tab, Alert,
  TextField, MenuItem, Divider, Box, Stack,
} from "@mui/material";
import ConferenceSettingsPanel from './components/ConferenceSettingsPanel';
import AccessRequestsPanel from './components/AccessRequestsPanel';
import EditRegistrantsPanel from './components/EditRegistrantsPanel';
import LocalityCoordinatorsPanel from './components/LocalityCoordinatorsPanel';
import ManageAccessPanel from './components/ManageAccessPanel';
import PaymentViewPanel from './components/PaymentViewPanel';
import MerchandisePanel from './components/MerchandisePanel';
import AllergiesPanel from './components/AllergiesPanel';

ModuleRegistry.registerModules([AllCommunityModule]);

const FN_NAME = "listParticipants"; // your deployed onCall function
// sp26 predates the unified `registrants` collection (see functions/index.js)
// and still lives in its own named collection - it's the one fixed "legacy"
// option alongside whatever conferences exist in Firestore's `conferences`
// collection.
const LEGACY_VIEW_OPTIONS = [{ id: 'sp26', mode: 'legacy', label: 'sp26 (legacy)' }];
const ADMIN_DASHBOARD_ROLES = ["ADMIN", "EDITOR", "VIEWER", "OWNER"];
// Matches firestore.rules: conferences/* accepts ADMIN/EDITOR/OWNER writes,
// but settings/active (which conference is live) and granting/denying access
// requests fall through to the stricter ADMIN/OWNER-only /{document=**} rule.
const CONFERENCE_EDIT_ROLES = ["ADMIN", "EDITOR", "OWNER"];
const CONFERENCE_ACTIVATE_ROLES = ["ADMIN", "OWNER"];
const MANAGE_ACCESS_ROLES = ["ADMIN", "OWNER"];
// Matches REGISTRANT_EDIT_ROLES in functions/index.js.
const REGISTRANT_EDIT_ROLES = ["ADMIN", "EDITOR", "OWNER"];
// Matches REFUND_ROLES in functions/index.js - stricter than
// REGISTRANT_EDIT_ROLES since this moves real money via PayPal.
const REFUND_ROLES = ["ADMIN", "OWNER"];
// Matches BULK_UPLOAD_ROLES in functions/index.js - creates real registrant
// records directly with no payment step, so it's gated the same as refunds
// rather than the broader REGISTRANT_EDIT_ROLES.
const BULK_UPLOAD_ROLES = ["ADMIN", "OWNER"];
// Matches LOCALITY_DIGEST_SEND_ROLES in functions/index.js - the locality
// directory itself is edited under CONFERENCE_EDIT_ROLES (reused below),
// but actually emailing coordinators is gated narrower, same tier as
// refunds/bulk upload.
const LOCALITY_DIGEST_SEND_ROLES = ["ADMIN", "OWNER"];
// Matches PAYMENT_VIEW_ROLES/FULL_PAYMENT_VIEW_ROLES in functions/index.js.
// VIEWER_LOCALITY isn't in ADMIN_DASHBOARD_ROLES above (it's added
// separately to `hasAccess` below) - it sees Signups and Payment View,
// both locality-restricted server-side, but nothing else (no Edit
// Registrants, Conference Settings, etc.).
const PAYMENT_VIEW_ROLES = ["VIEWER_LOCALITY", "EDITOR", "ADMIN", "OWNER"];
const FULL_PAYMENT_VIEW_ROLES = ["EDITOR", "ADMIN", "OWNER"];
// Matches listMerchandise in functions/index.js - same visibility as
// Signups (it's a headcount, not financial data).
const MERCHANDISE_VIEW_ROLES = [...ADMIN_DASHBOARD_ROLES, "VIEWER_LOCALITY"];
// Matches the role list listAllergies uses in functions/index.js.
const ALLERGIES_VIEW_ROLES = [...ADMIN_DASHBOARD_ROLES, "VIEWER_LOCALITY"];

const call = httpsCallable(functions, FN_NAME);

function friendlyAuthError(error) {
  const messages = {
    'auth/unauthorized-domain': 'This domain is not authorized for sign-in yet. An admin needs to add it under Firebase Console -> Authentication -> Settings -> Authorized domains.',
    'auth/email-already-in-use': 'An account with that email already exists - try signing in instead.',
    'auth/invalid-email': 'That email address looks invalid.',
    'auth/weak-password': 'Password is too weak - use at least 8 characters.',
    'auth/wrong-password': 'Incorrect email or password.',
    'auth/user-not-found': 'Incorrect email or password.',
    'auth/invalid-credential': 'Incorrect email or password.',
  };
  return messages[error.code] || error.message;
}

// Custom hook: tracks the signed-in Firebase user, their role claims, and (if
// they have no role yet) their dashboard access request status. The first
// time anyone signs in - Google or email/password - without a role, a
// pending request is filed automatically at adminRequests/{uid}; an
// ADMIN/OWNER reviews it from the Access Requests tab (see
// AccessRequestsPanel + approveAdminRequest/denyAdminRequest in
// functions/index.js). There is no way to grant yourself a role directly.
function useAdminAuth() {
  const [authState, setAuthState] = useState({ status: 'loading', user: null, roles: [], locality: null, requestStatus: null });

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAuthState({ status: 'signed-out', user: null, roles: [], locality: null, requestStatus: null });
        return;
      }
      const tokenResult = await user.getIdTokenResult();
      const roles = tokenResult.claims.roles || [];
      const locality = tokenResult.claims.locality || null;

      if (roles.length > 0) {
        setAuthState({ status: 'signed-in', user, roles, locality, requestStatus: null });
        return;
      }

      try {
        const ref = doc(db, 'adminRequests', user.uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          // For email/password sign-ups, displayName may not be set yet at
          // this exact moment - handleRegister below follows up with a
          // narrow displayName-only write once it's confirmed set (see
          // firestore.rules for why that's the one field a requester can
          // update on their own request after creating it).
          await setDoc(ref, { email: user.email, displayName: user.displayName || null, status: 'pending', requestedAt: serverTimestamp() });
          setAuthState({ status: 'signed-in', user, roles, locality, requestStatus: 'pending' });
        } else {
          setAuthState({ status: 'signed-in', user, roles, locality, requestStatus: snap.data().status });
        }
      } catch (error) {
        console.error('Failed to load/file access request:', error);
        setAuthState({ status: 'signed-in', user, roles, locality, requestStatus: 'error' });
      }
    });
  }, []);

  return authState;
}

export default function ParticipantsGrid() {
  const authState = useAdminAuth();
  // VIEWER_LOCALITY sees Signups too (locality-restricted server-side in
  // listParticipants), so it's included here alongside the regular
  // dashboard roles - every use of hasAccess below is Signups-specific.
  const hasAccess = authState.roles.some((r) => [...ADMIN_DASHBOARD_ROLES, "VIEWER_LOCALITY"].includes(r));
  const canEditConference = authState.roles.some((r) => CONFERENCE_EDIT_ROLES.includes(r));
  const canActivateConference = authState.roles.some((r) => CONFERENCE_ACTIVATE_ROLES.includes(r));
  const canManageAccess = authState.roles.some((r) => MANAGE_ACCESS_ROLES.includes(r));
  const canEditRegistrants = authState.roles.some((r) => REGISTRANT_EDIT_ROLES.includes(r));
  const canRefund = authState.roles.some((r) => REFUND_ROLES.includes(r));
  const canBulkUpload = authState.roles.some((r) => BULK_UPLOAD_ROLES.includes(r));
  const canSendLocalityDigest = authState.roles.some((r) => LOCALITY_DIGEST_SEND_ROLES.includes(r));
  const canViewPayments = authState.roles.some((r) => PAYMENT_VIEW_ROLES.includes(r));
  const hasFullPaymentView = authState.roles.some((r) => FULL_PAYMENT_VIEW_ROLES.includes(r));
  const canViewMerchandise = authState.roles.some((r) => MERCHANDISE_VIEW_ROLES.includes(r));
  const isLocalityOnlyMerchandise = canViewMerchandise && !authState.roles.some((r) => ADMIN_DASHBOARD_ROLES.includes(r));
  const canViewAllergies = authState.roles.some((r) => ALLERGIES_VIEW_ROLES.includes(r));
  const isLocalityOnlyAllergies = canViewAllergies && !authState.roles.some((r) => ADMIN_DASHBOARD_ROLES.includes(r));
  // True only when VIEWER_LOCALITY is what's granting Signups access (not
  // one of the regular ADMIN_DASHBOARD_ROLES) - used to show a heads-up that
  // the list is restricted to their locality.
  const isLocalityOnlySignups = hasAccess && !authState.roles.some((r) => ADMIN_DASHBOARD_ROLES.includes(r));
  const [tab, setTab] = useState('signups');

  // Data state
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [loading, setLoading] = useState(false);
  const [localities, setLocalities] = useState([])
  const [localityFilter, setLocalityFilter] = useState([])
  const [serviceFilter, setServiceFilter] = useState([])
  const [gridApi, setGridApi] = useState(null)
  const [viewOptions, setViewOptions] = useState(LEGACY_VIEW_OPTIONS)
  const [viewingId, setViewingId] = useState(null)
  const [colDefs] = useState([
    {
      headerName: "Row",
      valueGetter: "node.rowIndex + 1",
      width: 70,
      maxWidth: 90,
    },
    { field: "firstName" },
    { field: "lastName" },
    {
      field: "gender",
      width: 70,
      maxWidth: 90,
    },
    { field: "status" },
    { field: "school" },
    { field: "participation" },
    { field: "locality" },
    {
      field: "services",
      headerName: "Services",
      width: 220,
      valueFormatter: (params) => (Array.isArray(params.value) ? params.value.join(", ") : ""),
    },
  ]);

  // Which conference to show defaults to whatever's currently active
  // (settings/active), with every conferences/{id} doc plus the fixed
  // legacy sp26 collection offered as alternatives.
  useEffect(() => {
    if (!hasAccess) return;
    (async () => {
      try {
        const [activeSnap, confsSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'active')),
          getDocs(firestoreCollection(db, 'conferences')),
        ]);
        const newOptions = confsSnap.docs.map((d) => ({ id: d.id, mode: 'new', label: d.id }));
        const options = [...newOptions, ...LEGACY_VIEW_OPTIONS];
        setViewOptions(options);
        const active = activeSnap.data()?.conferenceId;
        setViewingId(active || options[0]?.id || null);
      } catch (error) {
        console.error('Failed to load conference list:', error);
        setViewOptions(LEGACY_VIEW_OPTIONS);
        setViewingId(LEGACY_VIEW_OPTIONS[0].id);
      }
    })();
  }, [hasAccess]);

  // Fetch page (simple, no pagination)

  useEffect(() => {
    if (!hasAccess || !viewingId) return;
    const option = viewOptions.find((o) => o.id === viewingId);
    const fetchData = async () => {
      setLoading(true)
      try {
        const params = option?.mode === 'legacy' ? { collection: viewingId } : { conferenceId: viewingId };
        const res = await call(params);
        const items = res.data?.items
        setData(items);
        setLocalities([...new Set(items.map(person => person.locality))]);
      } catch (error) {
        console.error('Failed to load participants:', error);
        setData([]);
        setLocalities([]);
      }
      finally {
        setLoading(false)
      }
    };
    fetchData()
  }, [hasAccess, viewingId, viewOptions]);

  useEffect(() => {
    const filteredItems = data.filter(item =>
      (localityFilter.length === 0 || localityFilter.includes(item.locality))
      && (serviceFilter.length === 0 || (Array.isArray(item.services) && item.services.some(s => serviceFilter.includes(s))))
    );
    setFilteredData(filteredItems)
  }, [localityFilter, serviceFilter, data])

  // Options + controlled value for react-select
  const localityOptions = useMemo(
    () => localities.map(loc => ({ value: loc, label: loc })),
    [localities]
  );

  const localityValue = useMemo(
    () => localityFilter.map(loc => ({ value: loc, label: loc })),
    [localityFilter]
  );

  const serviceOptions = useMemo(() => {
    const all = new Set();
    data.forEach((item) => { if (Array.isArray(item.services)) item.services.forEach((s) => all.add(s)); });
    return [...all].map((s) => ({ value: s, label: s }));
  }, [data]);

  const serviceValue = useMemo(
    () => serviceFilter.map((s) => ({ value: s, label: s })),
    [serviceFilter]
  );

  const handleSortChanged = useCallback((e) => {
    e.api.refreshCells({ columns: ["rowIndex"] });
  }, []);

  const handleExportCsv = useCallback(() => {
    gridApi?.exportDataAsCsv({ fileName: `signups-${viewingId || 'export'}.csv` });
  }, [gridApi, viewingId]);

  const [authMode, setAuthMode] = useState('signin'); // 'signin' | 'register'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authError, setAuthError] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [resetInfo, setResetInfo] = useState(null);

  const handleGoogleSignIn = () => {
    setAuthError(null);
    signInWithPopup(auth, new GoogleAuthProvider()).catch((error) => {
      console.error('Sign-in failed:', error);
      setAuthError(friendlyAuthError(error));
    });
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setAuthBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Email sign-in failed:', error);
      setAuthError(friendlyAuthError(error));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError(null);
    if (!name.trim()) {
      setAuthError('Please enter your name.');
      return;
    }
    if (password !== confirmPassword) {
      setAuthError('Passwords do not match.');
      return;
    }
    setAuthBusy(true);
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      // useAdminAuth's onAuthStateChanged handler files the pending access
      // request as soon as this resolves - possibly before displayName is
      // set below, so follow up with a direct write to be sure the name
      // ends up on the request either way (firestore.rules allows a
      // requester to update only this one field on their own request).
      await updateProfile(credential.user, { displayName: name.trim() });
      try {
        await setDoc(doc(db, 'adminRequests', credential.user.uid), { displayName: name.trim() }, { merge: true });
      } catch (mergeError) {
        // Non-fatal: if useAdminAuth's listener hasn't created the request
        // doc yet, this update can transiently fail - but that means it
        // will read the (by-then-set) displayName itself when it does.
        console.warn('Could not attach display name to the new access request:', mergeError);
      }
    } catch (error) {
      console.error('Registration failed:', error);
      setAuthError(friendlyAuthError(error));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleRefreshAccess = async () => {
    if (!authState.user) return;
    await authState.user.getIdToken(true);
    window.location.reload();
  };

  const handleForgotPassword = async () => {
    setAuthError(null);
    setResetInfo(null);
    if (!email) {
      setAuthError('Enter your email above first, then click "Forgot password?" again.');
      return;
    }
    setAuthBusy(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetInfo(`If an account exists for ${email}, a password reset email has been sent.`);
    } catch (error) {
      console.error('Failed to send password reset email:', error);
      // Don't reveal whether the account exists - same message either way.
      if (error.code === 'auth/user-not-found') {
        setResetInfo(`If an account exists for ${email}, a password reset email has been sent.`);
      } else {
        setAuthError(friendlyAuthError(error));
      }
    } finally {
      setAuthBusy(false);
    }
  };

  if (authState.status === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (authState.status === 'signed-out') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', py: 4 }}>
        <Container maxWidth="xs">
          <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
            <Typography component="h1" variant="h5" align="center" fontWeight={600} gutterBottom>
              Admin Dashboard
            </Typography>
            <Typography align="center" color="text.secondary" sx={{ mb: 3 }}>
              Sign in to manage signups and conference settings
            </Typography>

            <Tabs
              value={authMode}
              onChange={(e, v) => { setAuthMode(v); setAuthError(null); }}
              variant="fullWidth"
              sx={{ mb: 3 }}
            >
              <Tab value="signin" label="Sign In" />
              <Tab value="register" label="Register" />
            </Tabs>

            <Button fullWidth variant="outlined" onClick={handleGoogleSignIn} sx={{ mb: 2 }}>
              Continue with Google
            </Button>

            <Divider sx={{ mb: 2 }}>or</Divider>

            <Box component="form" onSubmit={authMode === 'signin' ? handleEmailSignIn : handleRegister}>
              <Stack spacing={2}>
                {authMode === 'register' && (
                  <TextField
                    label="Full name" fullWidth required autoComplete="name"
                    value={name} onChange={(e) => setName(e.target.value)}
                  />
                )}
                <TextField
                  label="Email" type="email" fullWidth required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  label="Password" type="password" fullWidth required
                  autoComplete={authMode === 'signin' ? 'current-password' : 'new-password'}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                />
                {authMode === 'signin' && (
                  <Button size="small" sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                    onClick={handleForgotPassword} disabled={authBusy}>
                    Forgot password?
                  </Button>
                )}
                {authMode === 'register' && (
                  <TextField
                    label="Confirm password" type="password" fullWidth required
                    autoComplete="new-password"
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                )}
                <Button type="submit" fullWidth variant="contained" disabled={authBusy}>
                  {authMode === 'signin' ? 'Sign in' : 'Register'}
                </Button>
              </Stack>
            </Box>

            {authMode === 'register' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                After registering, an admin will need to approve your account before you can view the dashboard.
              </Typography>
            )}

            {authError && <Alert severity="error" sx={{ mt: 2 }}>{authError}</Alert>}
            {resetInfo && <Alert severity="success" sx={{ mt: 2 }}>{resetInfo}</Alert>}
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!hasAccess && !canViewPayments) {
    const status = authState.requestStatus;
    let title = 'Access pending';
    let message = "Your request for dashboard access is waiting on approval. Check back soon, or contact Preston Wang.";
    let showRefresh = false;

    if (status === 'approved') {
      title = 'Access approved';
      message = 'Your access was approved - refresh to pick it up.';
      showRefresh = true;
    } else if (status === 'denied') {
      title = 'Access denied';
      message = 'Your request for dashboard access was denied. Contact Preston Wang if you think this is a mistake.';
    } else if (status === 'error') {
      title = 'Something went wrong';
      message = 'Could not check your access request status. Try signing out and back in.';
    }

    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
        <Container maxWidth="xs">
          <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              {title}
            </Typography>
            <Typography align="center" color="text.secondary" sx={{ mb: 2 }}>
              {message}
            </Typography>
            <Typography align="center" variant="body2" sx={{ mb: 3 }}>
              Signed in as {authState.user.email}
            </Typography>
            <Stack spacing={1}>
              {showRefresh && (
                <Button fullWidth variant="contained" onClick={handleRefreshAccess}>Refresh access</Button>
              )}
              <Button fullWidth variant="outlined" onClick={() => signOut(auth)}>Sign out</Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <React.Fragment>
        <div style={{ padding: 12, minHeight: "100vh", boxSizing: "border-box" }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tabs
                value={tab} onChange={(e, value) => setTab(value)}
                variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
              >
                {hasAccess && <Tab value="signups" label="Signups" />}
                {canEditRegistrants && <Tab value="editRegistrants" label="Edit Registrants" />}
                {canViewPayments && <Tab value="payments" label="Payment View" />}
                {canViewMerchandise && <Tab value="merchandise" label="Merchandise" />}
                {canViewAllergies && <Tab value="allergies" label="Allergies" />}
                {canActivateConference && <Tab value="conference" label="Conference Settings" />}
                {canEditConference && <Tab value="localities" label="Localities" />}
                {canManageAccess && <Tab value="access" label="Access Requests" />}
                {canManageAccess && <Tab value="manageAccess" label="Manage Access" />}
              </Tabs>
            </Box>
            <Button size="small" onClick={() => signOut(auth)} sx={{ flexShrink: 0 }}>Sign out</Button>
          </div>

          {tab === 'signups' ? (
            <div style={{ height: "calc(100vh - 80px)", display: 'flex', flexDirection: 'column' }}>
              {isLocalityOnlySignups && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  You're viewing signups for <strong>{authState.locality}</strong> only.
                </Alert>
              )}
              <div style={{ marginTop: 12, maxWidth: 300 }}>
                <TextField
                  select fullWidth size="small" label="Viewing conference"
                  value={viewingId || ''}
                  onChange={(e) => setViewingId(e.target.value)}
                >
                  {viewOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>{o.label || o.id}</MenuItem>
                  ))}
                </TextField>
              </div>
              <div style={{ marginTop: 12, marginBottom: 12, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ minWidth: 240, flex: 1, maxWidth: 400 }}>
                  <Select
                    value={localityValue}
                    isMulti
                    name="locality"
                    options={localityOptions
                    }
                    className="basic-multi-select"
                    classNamePrefix="select"
                    onChange={(values) => {
                      setLocalityFilter(values ? values.map(v => v.value) : [])
                    }}
                    placeholder={`select locality`}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </div>
                <div style={{ minWidth: 240, flex: 1, maxWidth: 400 }}>
                  <Select
                    value={serviceValue}
                    isMulti
                    name="services"
                    options={serviceOptions}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    onChange={(values) => {
                      setServiceFilter(values ? values.map(v => v.value) : [])
                    }}
                    placeholder="select service"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </div>
                <Button variant="outlined" size="small" onClick={handleExportCsv} disabled={!gridApi}>
                  Export CSV
                </Button>
              </div>
              <div
                className="ag-theme-quartz"
                style={{ width: "100%", flex: 1 }}
              >
                <AgGridReact
                  rowData={filteredData} loading={loading} columnDefs={colDefs} theme={themeQuartz}
                  onSortChanged={handleSortChanged} onGridReady={(params) => setGridApi(params.api)}
                />
              </div>
            </div>
          ) : tab === 'editRegistrants' ? (
            <div style={{ marginTop: 20 }}>
              <EditRegistrantsPanel canRefund={canRefund} canBulkUpload={canBulkUpload} />
            </div>
          ) : tab === 'payments' ? (
            <div style={{ marginTop: 20 }}>
              <PaymentViewPanel scopedLocality={!hasFullPaymentView ? authState.locality : null} />
            </div>
          ) : tab === 'merchandise' ? (
            <div style={{ marginTop: 20 }}>
              <MerchandisePanel scopedLocality={isLocalityOnlyMerchandise ? authState.locality : null} />
            </div>
          ) : tab === 'allergies' ? (
            <div style={{ marginTop: 20 }}>
              <AllergiesPanel scopedLocality={isLocalityOnlyAllergies ? authState.locality : null} />
            </div>
          ) : tab === 'conference' ? (
            <div style={{ marginTop: 20 }}>
              <ConferenceSettingsPanel canEdit={canEditConference} canActivate={canActivateConference} />
            </div>
          ) : tab === 'localities' ? (
            <div style={{ marginTop: 20 }}>
              <LocalityCoordinatorsPanel canEdit={canEditConference} canSend={canSendLocalityDigest} />
            </div>
          ) : tab === 'access' ? (
            <div style={{ marginTop: 20 }}>
              <AccessRequestsPanel />
            </div>
          ) : (
            <div style={{ marginTop: 20 }}>
              <ManageAccessPanel />
            </div>
          )}
        </div>
    </React.Fragment>
  );
}
